<template>
  <base-dialog v-model="isVisible" max-width="700">
    <v-card class="scanner-upload-card">
      <v-card-title>{{ $t('dialog.title') }}</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="formValid">
          <v-row class="mb-n10">
            <v-col cols="12">
              <v-combobox
                :items="placeOptions"
                v-model="placeNameModel"
                :label="$t('dialog.place_name')"
                :rules="validationRules.placeName"
                :disabled="!!place"
                required
              />
            </v-col>
          </v-row>

          <v-row class="mb-n5">
            <v-col cols="12">
              <v-combobox
                :items="trapOptions"
                v-model="formData.trapNo"
                :label="$t('dialog.trap_no')"
                :rules="validationRules.trapNo"
                required
                data-testid="scanner_trap_no"
              />
            </v-col>
          </v-row>

          <v-row class="mb-n5">
            <v-col cols="4">
              <date-picker-dropdown
                type="date"
                v-model="formData.installDate"
                :date-picker-props="{ 'data-testid': 'scanner_install_picker' }"
              >
                <template #activator="{ on, attrs, displayValue }">
                  <text-field
                    :value="displayValue"
                    :label="$t('dialog.install_date')"
                    :rules="validationRules.installDate"
                    required
                    readonly
                    v-on="on"
                    v-bind="attrs"
                    data-testid="scanner_install_date"
                  />
                </template>
              </date-picker-dropdown>
            </v-col>
            <v-col cols="4">
              <date-picker-dropdown
                type="date"
                v-model="formData.collectDate"
                :date-picker-props="{ 'data-testid': 'scanner_collect_picker' }"
              >
                <template #activator="{ on, attrs, displayValue }">
                  <text-field
                    :value="displayValue"
                    :label="$t('dialog.collect_date')"
                    :rules="validationRules.collectDate"
                    required
                    readonly
                    v-on="on"
                    v-bind="attrs"
                    data-testid="scanner_collect_date"
                  />
                </template>
              </date-picker-dropdown>
            </v-col>
            <v-col cols="4">
              <month-field
                v-model="formData.monthLabel"
                :label="$t('dialog.month_label')"
                :rules="validationRules.monthLabel"
                required
                :data-testid="'scanner_month_label'"
              />
            </v-col>
          </v-row>

          <div
            class="drop-zone"
            :class="{ 'drag-over': isDragOver }"
            @drop="onDrop"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @click="openFileSelector"
            data-testid="scanner_drop_zone"
          >
            <v-icon size="48" color="grey">mdi-camera-plus</v-icon>
            <p class="mt-2 mb-0 d-flex justify-center align-baseline">
              {{ $t('dialog.select_images') }}
              <v-tooltip bottom max-width="400">
                <template #activator="{ on, attrs }">
                  <v-icon
                    small
                    class="info-icon ml-1"
                    :aria-label="$t('dialog.image_spec_label')"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>
                  <p class="mb-2">{{ $t('dialog.image_spec_intro') }}</p>
                  <p class="mb-1 font-weight-bold">{{ $t('dialog.image_spec_resolution_title') }}</p>
                  <p class="mb-2">{{ $t('dialog.image_spec_resolution_desc') }}</p>
                  <p class="mb-1 font-weight-bold">{{ $t('dialog.image_spec_size_title') }}</p>
                  <p class="mb-0">{{ $t('dialog.image_spec_size_desc') }}</p>
                </div>
              </v-tooltip>
            </p>
            <p class="caption">{{ $t('dialog.drag_drop_hint') }}</p>

            <v-alert
              v-if="warningMessage"
              type="warning"
              outlined
              dense
              class="mt-4 text-left text-body-2"
              data-testid="scanner_warning"
            >
              {{ warningMessage }}
            </v-alert>
            <v-alert
              v-if="errorMessage"
              type="error"
              dense
              class="mt-4 text-left text-body-2"
              data-testid="scanner_error"
            >
              {{ errorMessage }}
            </v-alert>

            <input
              ref="fileInput"
              type="file"
              accept=".jpg,.jpeg"
              multiple
              style="display: none"
              data-testid="scanner-file-input"
              @change="onFileInputChange"
            />
          </div>

          <div v-if="selectedFiles.length > 0" class="mt-4">
            <h4>{{ $t('dialog.selected_images') }}</h4>
            <v-list dense class="selected-files-list">
              <v-list-item
                v-for="(file, index) in selectedFiles"
                :key="index"
                class="selected-file-item"
              >
                <v-list-item-avatar>
                  <v-icon color="primary">mdi-file-image</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="text-truncate">
                    {{ file.name }}
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    data-testid="scanner_remove_file"
                    @click="removeFile(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="closeDialog">{{ $t('dialog.cancel') }}</v-btn>
        <v-btn
          color="primary"
          :loading="uploading"
          :disabled="!isFormValid"
          @click="uploadImages"
          data-testid="scanner_submit"
        >
          {{ $t('dialog.upload') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </base-dialog>
</template>

<i18n lang="yaml">
ja:
  dialog:
    title: '画像アップロード＆解析'
    place_name: '設置場所'
    trap_no: 'トラップNo'
    install_date: '設置日'
    collect_date: '回収日'
    month_label: '月ラベル'
    select_images: 'JPEG画像を選択またはクリック'
    selected_images: '選択された画像'
    cancel: 'キャンセル'
    upload: '解析開始'
    drag_drop_hint: '画像ファイルをここにドラッグ&ドロップできます'
    image_spec_label: '画像仕様'
    image_spec_intro: '正確な昆虫のカウントと種類の識別精度を確保するため、アップロード画像は以下の要件を満たす必要があります。'
    image_spec_resolution_title: '必須解像度: 2400 dpi (Dots Per Inch) 程度'
    image_spec_resolution_desc: '虫の細部（脚、触角、体の模様など）をAIが正確に識別し、高い同定精度を達成するために、この高解像度が不可欠です。'
    image_spec_size_title: '最大サイズ: A3サイズまで'
    image_spec_size_desc: 'スキャンデータの処理負荷とアップロード時間を最適化するための最大サイズです。'
  validation:
    install_date_required: '設置日を入力してください'
    collect_date_required: '回収日を入力してください'
    collect_date_must_be_after_install_date: '回収日は設置日以降の日付を設定してください'
    month_label_required: '月ラベルを入力してください'
    invalid_file_type: 'JPEG形式のファイルのみアップロードできます'
    image_size_too_large: '解析可能な画像サイズを超えています'
    jpeg_progressive_warning: 'プログレッシブ形式の画像が含まれています。解析結果が通常の JPEG と異なる可能性があります。'
    jpeg_lossless_unsupported: '対応していない形式です'
  message:
    upload_success: 'スキャナ画像のアップロードが完了しました'
    upload_failed: 'アップロードに失敗しました。しばらくしてから再度お試しください。'

en:
  dialog:
    title: 'Image Upload & Analysis'
    place_name: 'Installation Site'
    trap_no: 'Trap No'
    install_date: 'Installation Date'
    collect_date: 'Collection Date'
    month_label: 'Month Label'
    select_images: 'Select JPEG images or click'
    selected_images: 'Selected Images'
    cancel: 'Cancel'
    upload: 'Start Analysis'
    drag_drop_hint: 'You can drag & drop image files here'
    image_spec_label: 'Image specifications'
    image_spec_intro: >-
      To ensure the highest possible accuracy in insect counting and species identification,
      your uploaded image must meet the following criteria:
    image_spec_resolution_title: 'Minimum Resolution: 2400 dpi (Dots Per Inch) or Higher'
    image_spec_resolution_desc: >-
      This high resolution is crucial for the AI to clearly recognize the fine details
      (legs, antennae, body patterns) of the insects, enabling high identification precision.
    image_spec_size_title: 'Maximum Size: Up to A3 size'
    image_spec_size_desc: 'This limit is set to optimize the data processing load and file upload speed.'
  validation:
    install_date_required: 'Please enter installation date'
    collect_date_required: 'Please enter collection date'
    collect_date_must_be_after_install_date: 'Collection date must be on or after installation date'
    month_label_required: 'Please enter month label'
    invalid_file_type: 'Only JPEG format files can be uploaded'
    image_size_too_large: 'Image size exceeds analyzable limit'
    jpeg_progressive_warning: 'A progressive JPEG image is included. Analysis results may differ from a standard JPEG.'
    jpeg_lossless_unsupported: 'Unsupported format'
  message:
    upload_success: 'Scanner image upload completed successfully'
    upload_failed: 'Upload failed. Please try again later.'
</i18n>

<script>
import BaseDialog from '@/components/atoms/BaseDialog';
import MonthField from '@/components/molecules/MonthField';
import DatePickerDropdown from '@/components/atoms/DatePickerDropdown';
import TextField from '@/components/atoms/TextField';
import FormValidations from '@/components/Form/mixins/form-validations';
import { VALIDATION_REASON, VALIDATION_WARNING } from '@/libs/imageValidator';

const MAX_S_TYPE_SHORT_SIDE = 50248; // 短辺最大
// 総ピクセル上限（23億）
// Lambda 10GBメモリ制限を考慮（画像展開時に約1.5倍のメモリが必要）
// 例: A2横 2400dpi (55616 × 40256 ≈ 22.4億ピクセル) が処理可能
const MAX_S_TYPE_TOTAL_PIXELS = 2_300_000_000;

export default {
  name: 'scanner-upload-dialog',
  inject: ['api', 'imageValidator'],
  mixins: [FormValidations],
  components: {
    BaseDialog,
    MonthField,
    DatePickerDropdown,
    TextField,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    place: {
      type: Object,
      default: null,
    },
    places: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      formValid: false,
      formData: {
        placeName: '',
        trapNo: '',
        installDate: '',
        collectDate: '',
        monthLabel: '',
      },
      selectedFiles: [],
      progressiveFiles: [],
      isDragOver: false,
      uploading: false,
      errorMessage: '',
      warningMessage: '',
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    placeNameModel: {
      get() {
        return this.place ? this.place.name : this.formData.placeName;
      },
      set(value) {
        this.formData.placeName = value;
      },
    },
    placeOptions() {
      return this.places.map(p => p.name).sort();
    },
    validationRules() {
      const { installDate } = this.formData; // 依存関係を明確にする
      return {
        placeName: !this.place
          ? [this.requiredTrimmed(this.$t('dialog.place_name'))]
          : [],
        trapNo: [this.requiredTrimmed(this.$t('dialog.trap_no'))],
        installDate: [this.required(this.$t('dialog.install_date'))],
        collectDate: [
          this.required(this.$t('dialog.collect_date')),
          v => {
            if (!v || !installDate) return true;
            const isValid = new Date(v) >= new Date(installDate);
            return isValid || this.$t('validation.collect_date_must_be_after_install_date');
          },
        ],
        monthLabel: [this.required(this.$t('dialog.month_label'))],
      };
    },
    selectedPlace() {
      // placeプロップがある場合（StationPlaceEventsから）
      if (this.place) {
        return this.place;
      }

      // 設置場所名が入力されている場合（StationPlacesから）
      if (this.formData.placeName) {
        return this.places.find(p => p.name === this.formData.placeName);
      }

      return null;
    },
    trapOptions() {
      if (this.selectedPlace && this.selectedPlace.trap_nos) {
        return [...this.selectedPlace.trap_nos].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      }
      return [];
    },
    isFormValid() {
      return (
        this.formValid &&
        this.selectedFiles.length > 0
      );
    },
    hasProgressiveFile() {
      return this.progressiveFiles.length > 0;
    },
  },
  watch: {
    'formData.installDate'(newDate) {
      if (newDate) {
        this.formData.monthLabel = newDate.substring(0, 7);
      }
      this.revalidateForm();
    },
    'formData.collectDate'() {
      this.revalidateForm();
    },
    value(newVal) {
      if (!newVal) this.resetForm();
    },
  },
  methods: {
    revalidateForm() {
      if (this.$refs.form) {
        this.$nextTick(() => {
          this.$refs.form.validate();
        });
      }
    },
    safeTranslate(key, fallback = '') {
      try {
        return this.$t(key);
      } catch (error) {
        return fallback;
      }
    },
    async onDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.errorMessage = '';
      const result = await this.processFiles(files);
      this.selectedFiles.push(...result.validFiles);
      this.progressiveFiles.push(...result.progressiveFiles);
      this.errorMessage = result.errorMessage;
      this.refreshWarningMessage();
    },

    onDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    onDragLeave() {
      this.isDragOver = false;
    },

    openFileSelector() {
      this.$refs.fileInput.click();
    },

    async onFileInputChange(event) {
      const files = Array.from(event.target.files);
      this.errorMessage = '';
      const result = await this.processFiles(files);
      this.selectedFiles.push(...result.validFiles);
      this.progressiveFiles.push(...result.progressiveFiles);
      this.errorMessage = result.errorMessage;
      this.refreshWarningMessage();
      event.target.value = '';
    },

    async processFiles(files) {
      const validationResults = await Promise.all(
        files.map(async (file) => {
          const validation = await this.isValidJpegFile(file);
          return { file, ...validation };
        }),
      );

      const validFiles = validationResults
        .filter(result => result.isValid)
        .map(result => result.file);

      // エラーの理由を分類してエラーメッセージを生成
      const invalidResults = validationResults.filter(result => !result.isValid);
      const hasLosslessJpeg = invalidResults.some(
        result => result.reason === VALIDATION_REASON.JPEG_LOSSLESS_UNSUPPORTED,
      );
      const hasOversizedImages = invalidResults.some(
        result => result.reason === VALIDATION_REASON.IMAGE_SIZE_TOO_LARGE,
      );
      const hasInvalidFileType = invalidResults.some(
        result => result.reason === VALIDATION_REASON.INVALID_FILE_TYPE,
      );

      let errorMessage = '';
      if (hasLosslessJpeg) {
        errorMessage = this.safeTranslate('validation.jpeg_lossless_unsupported', 'Unsupported format');
      } else if (hasOversizedImages) {
        errorMessage = this.safeTranslate('validation.image_size_too_large', 'Image size exceeds analyzable limit');
      } else if (hasInvalidFileType) {
        errorMessage = this.safeTranslate('validation.invalid_file_type', 'Only JPEG format files can be uploaded');
      }

      const progressiveFiles = validationResults
        .filter(result => result.warning === VALIDATION_WARNING.JPEG_PROGRESSIVE)
        .map(result => result.file);

      return {
        validFiles,
        errorMessage,
        progressiveFiles,
      };
    },

    async isValidJpegFile(file) {
      const basicValidation = await this.imageValidator.validateImageFile(file, { allowedTypes: ['jpeg'] });
      if (!basicValidation.isValid) return basicValidation;

      return this.imageValidator.validateImagePixelLimits(file, {
        maxShortSide: MAX_S_TYPE_SHORT_SIDE,
        maxTotalPixels: MAX_S_TYPE_TOTAL_PIXELS,
      });
    },

    removeFile(index) {
      const [removed] = this.selectedFiles.splice(index, 1);
      const progressiveIndex = this.progressiveFiles.indexOf(removed);
      if (progressiveIndex !== -1) {
        this.progressiveFiles.splice(progressiveIndex, 1);
      }
      this.refreshWarningMessage();
    },

    refreshWarningMessage() {
      this.warningMessage = this.hasProgressiveFile
        ? this.safeTranslate('validation.jpeg_progressive_warning', 'Progressive JPEG detected; results may differ')
        : '';
    },

    async uploadImages() {
      if (!this.isFormValid) {
        return;
      }

      this.uploading = true;
      this.errorMessage = '';

      try {
        const response = await this.api.createSTypeScannerEvent({
          place: this.placeNameModel,
          trap_no: this.formData.trapNo,
          installed_at: this.formData.installDate,
          collected_at: this.formData.collectDate,
          month_label: this.formData.monthLabel,
          file_count: this.selectedFiles.length,
        });

        await this.uploadFiles(response.photos);

        this.$emit('uploaded', {
          message: this.$t('message.upload_success'),
          files: this.selectedFiles.length,
          event: response.event,
        });
      } catch (error) {
        this.errorMessage = error.response?.data?.message || this.$t('message.upload_failed');
        this.uploading = false;
      }
    },

    async uploadFiles(presignedUrls) {
      const uploadPromises = this.selectedFiles.map((file, i) =>
        this.api.uploadToPresignedUrl(presignedUrls[i], file));
      await Promise.all(uploadPromises);
    },

    close() {
      this.closeDialog();
    },

    closeDialog() {
      this.isVisible = false;
    },

    resetForm() {
      this.formData = {
        placeName: '',
        trapNo: '',
        installDate: '',
        collectDate: '',
        monthLabel: '',
      };
      this.selectedFiles = [];
      this.progressiveFiles = [];
      this.errorMessage = '';
      this.warningMessage = '';
      this.uploading = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;
}

.drop-zone:hover,
.drop-zone.drag-over {
  border-color: #1976d2;
  background-color: #f5f5f5;
}

.selected-file-item {
  border-bottom: 1px solid #e0e0e0;
}

.selected-file-item:last-child {
  border-bottom: none;
}

.info-icon {
  cursor: none;
}

.selected-files-list {
  max-height: 200px;
  overflow-y: auto;
}

/* 代理ログインバナー（position: fixed, bottom: 20px）と重ならないようにモーダルの高さを制限 */
.scanner-upload-card {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}

.scanner-upload-card > ::v-deep .v-card__text {
  flex: 1;
  overflow-y: auto;
}
</style>
